import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Modal,
    Form,
    Input,
    Upload,
    message,
    Popconfirm,
    Tag,
    Image,
    Card,
    Row,
    Col,
    Select,
    Tooltip,
    Typography,
    Spin,
    Empty,
    GetProp
} from 'antd';
import {
    UploadOutlined,
    DeleteOutlined,
    SearchOutlined,
    PlusOutlined,
    TagsOutlined,
    ReloadOutlined,
    PictureOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { ApiService } from '../services/api';
import { Material, MaterialKeyword } from '../types';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file as Blob);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

const MaterialManagement: React.FC = () => {
    const [materials, setMaterials] = useState<Material[]>([]);
    const [keywords, setKeywords] = useState<MaterialKeyword[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });

    // 上传相关状态
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploadForm] = Form.useForm();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    // 关键字管理相关状态
    const [keywordModalVisible, setKeywordModalVisible] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
    const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);

    // 搜索相关状态
    const [searchKeyword, setSearchKeyword] = useState('');

    // 视图模式：table 或 grid
    const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');

    const [imageHost, setImageHost] = useState('');

    // 加载素材列表
    const loadMaterials = async (page: number = pagination.current, keyword: string = searchKeyword) => {
        setLoading(true);
        try {
            const response = await ApiService.getMaterialList({
                keyword: keyword || undefined,
                page,
                page_size: pagination.pageSize
            });

            if (response.code === 200) {
                setMaterials(response.data.materials || []);
                setPagination({
                    ...pagination,
                    current: response.data.pagination.page,
                    total: response.data.pagination.total
                });
                setImageHost(response.data.image_host || '');
            }
        } catch (error) {
            message.error('加载素材列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 加载关键字列表
    const loadKeywords = async () => {
        try {
            const response = await ApiService.getMaterialKeywordList({
                page: 1,
                page_size: 100
            });

            if (response.code === 200) {
                setKeywords(response.data.keywords || []);
            }
        } catch (error) {
            message.error('加载关键字列表失败');
        }
    };

    useEffect(() => {
        loadMaterials();
        loadKeywords();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 处理图片预览
    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as FileType);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    // 处理上传
    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.warning('请选择要上传的文件');
            return;
        }

        const formData = new FormData();

        // 添加文件
        fileList.forEach(file => {
            if (file.originFileObj) {
                formData.append('files', file.originFileObj);
            }
        });

        // 添加名称和描述（如果有）
        const values = uploadForm.getFieldsValue();
        if (values.names) {
            const names = values.names.split('\n').filter((n: string) => n.trim());
            names.forEach((name: string) => {
                formData.append('names', name.trim());
            });
        }
        if (values.descriptions) {
            const descriptions = values.descriptions.split('\n').filter((d: string) => d.trim());
            descriptions.forEach((desc: string) => {
                formData.append('descriptions', desc.trim());
            });
        }

        setLoading(true);
        try {
            const response = await ApiService.uploadMaterials(formData);

            if (response.code === 200) {
                const { success_count, failed_count } = response.data;
                message.success(`上传完成！成功: ${success_count}, 失败: ${failed_count}`);

                setUploadModalVisible(false);
                setFileList([]);
                uploadForm.resetFields();
                loadMaterials(1);
            }
        } catch (error) {
            message.error('上传失败');
        } finally {
            setLoading(false);
        }
    };

    // 删除素材
    const handleDelete = async (materialId: number) => {
        try {
            const response = await ApiService.deleteMaterial({ material_id: materialId });

            if (response.code === 200) {
                message.success('删除成功');
                loadMaterials();
            }
        } catch (error) {
            message.error('删除失败');
        }
    };

    // 打开关键字管理对话框
    const openKeywordModal = (material: Material) => {
        setSelectedMaterial(material);
        setSelectedKeywords([]);
        setKeywordModalVisible(true);
    };

    // 添加关键字
    const handleAddKeywords = async () => {
        if (!selectedMaterial || selectedKeywords.length === 0) {
            message.warning('请选择关键字');
            return;
        }

        try {
            const response = await ApiService.addMaterialKeywords({
                material_id: selectedMaterial.id,
                keywords: selectedKeywords
            });

            if (response.code === 200) {
                message.success(response.data.message);
                setKeywordModalVisible(false);
                loadMaterials();
                loadKeywords();
            }
        } catch (error) {
            message.error('添加关键字失败');
        }
    };

    // 删除关键字
    const handleRemoveKeyword = async (materialId: number, keywordId: number) => {
        try {
            const response = await ApiService.removeMaterialKeyword({
                material_id: materialId,
                keyword_id: keywordId
            });

            if (response.code === 200) {
                message.success('关键字删除成功');
                loadMaterials();
            }
        } catch (error) {
            message.error('删除关键字失败');
        }
    };

    // 搜索
    const handleSearch = () => {
        loadMaterials(1, searchKeyword);
    };

    // 渲染网格视图
    const renderGridView = () => {
        if (loading) {
            return (
                <Card>
                    <div style={{ textAlign: 'center', padding: '50px 0' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>加载中...</div>
                    </div>
                </Card>
            );
        }

        if (materials.length === 0) {
            return (
                <Card>
                    <Empty
                        image={<PictureOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                        description="暂无素材数据"
                    />
                </Card>
            );
        }

        return (
            <>
                <Card>
                    <Row gutter={[12, 12]}>
                        {materials.map(material => (
                            <Col key={material.id} xs={12} sm={8} md={6} lg={4} xl={4} xxl={3}>
                                <div
                                    style={{
                                        position: 'relative',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #d9d9d9',
                                        transition: 'all 0.3s',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    {/* 图片区域 */}
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '8px',
                                        height: '120px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative'
                                    }}>
                                        <Image
                                            width="100%"
                                            src={`${imageHost}${material.directory}`}
                                            alt={material.name}
                                            style={{
                                                maxHeight: '104px',
                                                objectFit: 'contain'
                                            }}
                                            placeholder={
                                                <div style={{
                                                    height: '104px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <Spin />
                                                </div>
                                            }
                                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                                        />
                                    </div>

                                    {/* 关键字显示区 */}
                                    <div style={{
                                        padding: '6px 8px',
                                        backgroundColor: '#fafafa',
                                        borderTop: '1px solid #f0f0f0',
                                        minHeight: '36px',
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '4px',
                                        alignItems: 'flex-start',
                                        overflow: 'hidden'
                                    }}>
                                        {material.keywords && material.keywords.length > 0 ? (
                                            material.keywords.slice(0, 3).map(kw => (
                                                <Tag
                                                    key={kw.id}
                                                    style={{
                                                        fontSize: '10px',
                                                        padding: '0 4px',
                                                        lineHeight: '16px',
                                                        height: '16px',
                                                        margin: 0
                                                    }}
                                                    color="blue"
                                                >
                                                    {kw.keyword}
                                                </Tag>
                                            ))
                                        ) : (
                                            <span style={{
                                                fontSize: '10px',
                                                color: '#ccc',
                                                lineHeight: '16px'
                                            }}>
                                                无关键字
                                            </span>
                                        )}
                                        {material.keywords && material.keywords.length > 3 && (
                                            <Tag style={{
                                                fontSize: '10px',
                                                padding: '0 4px',
                                                lineHeight: '16px',
                                                height: '16px',
                                                margin: 0
                                            }}>
                                                +{material.keywords.length - 3}
                                            </Tag>
                                        )}
                                    </div>

                                    {/* 素材信息区 */}
                                    <div style={{
                                        padding: '6px 8px',
                                        backgroundColor: '#f9f9f9',
                                        borderTop: '1px solid #f0f0f0',
                                        minHeight: '56px'
                                    }}>
                                        <Text
                                            style={{
                                                fontSize: '12px',
                                                color: '#333',
                                                fontWeight: 500,
                                                display: 'block',
                                                marginBottom: '4px'
                                            }}
                                            ellipsis={{ tooltip: material.name }}
                                        >
                                            {material.name}
                                        </Text>
                                        {/* <Text
                                            style={{
                                                fontSize: '10px',
                                                color: '#999',
                                                display: 'block'
                                            }}
                                        >
                                            {material.width}×{material.height}
                                        </Text> */}
                                        <Text
                                            style={{
                                                fontSize: '10px',
                                                color: '#999',
                                                display: 'block'
                                            }}
                                        >
                                            {material.file_size < 1024
                                                ? `${material.file_size}B`
                                                : material.file_size < 1024 * 1024
                                                    ? `${(material.file_size / 1024).toFixed(2)}KB`
                                                    : `${(material.file_size / 1024 / 1024).toFixed(2)}MB`}
                                        </Text>
                                    </div>

                                    {/* 底部操作栏 */}
                                    <div style={{
                                        padding: '6px 8px',
                                        backgroundColor: '#fff',
                                        borderTop: '1px solid #f0f0f0',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Text style={{
                                            fontSize: '11px',
                                            color: '#666'
                                        }}>
                                            ID: {material.id}
                                        </Text>
                                        <Space size="small">
                                            <Tooltip title="管理关键字">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<TagsOutlined />}
                                                    onClick={() => openKeywordModal(material)}
                                                    style={{
                                                        padding: '2px 4px',
                                                        fontSize: '12px',
                                                        height: 'auto',
                                                        color: '#1890ff'
                                                    }}
                                                />
                                            </Tooltip>
                                            <Tooltip title="删除素材">
                                                <Popconfirm
                                                    title="确定要删除这个素材吗？"
                                                    onConfirm={() => handleDelete(material.id)}
                                                    okText="确定"
                                                    cancelText="取消"
                                                >
                                                    <Button
                                                        type="text"
                                                        danger
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        style={{
                                                            padding: '2px 4px',
                                                            fontSize: '12px',
                                                            height: 'auto'
                                                        }}
                                                    />
                                                </Popconfirm>
                                            </Tooltip>
                                        </Space>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>

                    {/* 分页 */}
                    {pagination.total > pagination.pageSize && (
                        <div style={{ textAlign: 'center', marginTop: 24 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                    <Text type="secondary">
                                        第 {(pagination.current - 1) * pagination.pageSize + 1}-
                                        {Math.min(pagination.current * pagination.pageSize, pagination.total)} 条，
                                        共 {pagination.total} 条
                                    </Text>
                                </div>
                                <Space>
                                    <Button
                                        disabled={pagination.current === 1}
                                        onClick={() => {
                                            const newPage = pagination.current - 1;
                                            setPagination({ ...pagination, current: newPage });
                                            loadMaterials(newPage);
                                        }}
                                    >
                                        上一页
                                    </Button>
                                    <Text>第 {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)} 页</Text>
                                    <Button
                                        disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                                        onClick={() => {
                                            const newPage = pagination.current + 1;
                                            setPagination({ ...pagination, current: newPage });
                                            loadMaterials(newPage);
                                        }}
                                    >
                                        下一页
                                    </Button>
                                </Space>
                            </Space>
                        </div>
                    )}
                </Card>
            </>
        );
    };

    // 表格列定义
    const columns: ColumnsType<Material> = [
        {
            title: '预览',
            dataIndex: 'directory',
            key: 'preview',
            width: 100,
            render: (directory: string) => (
                <Image
                    width={60}
                    height={60}
                    src={`${imageHost}${directory}`}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                    style={{ objectFit: 'cover' }}
                />
            )
        },
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
            width: 200
        },
        {
            title: '尺寸',
            key: 'size',
            width: 100,
            render: (_, record) => `${record.width}x${record.height}`
        },
        {
            title: '文件大小',
            dataIndex: 'file_size',
            key: 'file_size',
            width: 100,
            render: (size: number) => {
                if (size < 1024) return `${size}B`;
                if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)}KB`;
                return `${(size / 1024 / 1024).toFixed(2)}MB`;
            }
        },
        {
            title: '关键字',
            dataIndex: 'keywords',
            key: 'keywords',
            render: (keywords: MaterialKeyword[] = []) => (
                <Space wrap>
                    {keywords.map(kw => (
                        <Tag
                            key={kw.id}
                            closable
                            onClose={(e) => {
                                e.preventDefault();
                                const material = materials.find(m =>
                                    m.keywords?.some(k => k.id === kw.id)
                                );
                                if (material) {
                                    handleRemoveKeyword(material.id, kw.id);
                                }
                            }}
                        >
                            {kw.keyword}
                        </Tag>
                    ))}
                </Space>
            )
        },
        // {
        //     title: '描述',
        //     dataIndex: 'description',
        //     key: 'description',
        //     ellipsis: true
        // },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (time: string) => new Date(time).toLocaleString('zh-CN')
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="管理关键字">
                        <Button
                            type="link"
                            icon={<TagsOutlined />}
                            onClick={() => openKeywordModal(record)}
                        >
                            关键字
                        </Button>
                    </Tooltip>
                    <Popconfirm
                        title="确定要删除这个素材吗？"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={12}>
                        <Space>
                            <Input
                                placeholder="搜索关键字"
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                                onPressEnter={handleSearch}
                                style={{ width: 300 }}
                            />
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleSearch}
                            >
                                搜索
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => {
                                    setSearchKeyword('');
                                    loadMaterials(1, '');
                                }}
                            >
                                重置
                            </Button>
                        </Space>
                    </Col>
                    <Col span={12} style={{ textAlign: 'right' }}>
                        <Space>
                            <Button.Group>
                                <Button
                                    type={viewMode === 'grid' ? 'primary' : 'default'}
                                    icon={<PictureOutlined />}
                                    onClick={() => setViewMode('grid')}
                                >
                                    网格
                                </Button>
                                <Button
                                    type={viewMode === 'table' ? 'primary' : 'default'}
                                    onClick={() => setViewMode('table')}
                                >
                                    表格
                                </Button>
                            </Button.Group>
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                onClick={() => setUploadModalVisible(true)}
                            >
                                上传素材
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {viewMode === 'grid' ? (
                    renderGridView()
                ) : (
                    <Table
                        columns={columns}
                        dataSource={materials}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            showTotal: (total) => `共 ${total} 条`,
                            onChange: (page, pageSize) => {
                                setPagination({ ...pagination, current: page, pageSize: pageSize || 20 });
                                loadMaterials(page);
                            }
                        }}
                    // scroll={{ x: 1500 }}
                    />
                )}
            </Card>

            {/* 上传素材对话框 */}
            <Modal
                title="上传素材"
                open={uploadModalVisible}
                onOk={handleUpload}
                onCancel={() => {
                    setUploadModalVisible(false);
                    setFileList([]);
                    uploadForm.resetFields();
                }}
                width={700}
                confirmLoading={loading}
            >
                <Form form={uploadForm} layout="vertical">
                    <Form.Item
                        label="选择文件"
                        required
                        tooltip="支持jpg, jpeg, png, gif, bmp, webp格式，最多50个文件"
                    >
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            onChange={({ fileList: newFileList }) => {
                                if (newFileList.length > 50) {
                                    message.warning('最多只能选择50个文件');
                                    return;
                                }
                                setFileList(newFileList);
                            }}
                            onPreview={handlePreview}
                            beforeUpload={(file) => {
                                const isImage = file.type?.startsWith('image/');
                                if (!isImage) {
                                    message.error('只能上传图片文件!');
                                    return false;
                                }
                                const isLt10M = file.size / 1024 / 1024 < 10;
                                if (!isLt10M) {
                                    message.error('图片大小不能超过 10MB!');
                                    return false;
                                }
                                return false; // 阻止自动上传
                            }}
                            multiple
                            accept="image/*"
                            maxCount={50}
                        >
                            {fileList.length >= 50 ? null : (
                                <button style={{ border: 0, background: 'none' }} type="button">
                                    <PlusOutlined />
                                    <div style={{ marginTop: 8 }}>上传图片</div>
                                </button>
                            )}
                        </Upload>
                        {previewImage && (
                            <Image
                                wrapperStyle={{ display: 'none' }}
                                preview={{
                                    visible: previewOpen,
                                    onVisibleChange: (visible) => setPreviewOpen(visible),
                                    afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                }}
                                src={previewImage}
                            />
                        )}
                        <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                            已选择 {fileList.length} 个文件
                        </div>
                    </Form.Item>

                    {/* <Form.Item
                        name="names"
                        label="素材名称"
                        tooltip="每行一个名称，与文件一一对应。如果不填，将使用文件名"
                    >
                        <TextArea
                            rows={4}
                            placeholder="春节素材&#10;新年素材&#10;喜庆素材"
                        />
                    </Form.Item>

                    <Form.Item
                        name="descriptions"
                        label="素材描述"
                        tooltip="每行一个描述，与文件一一对应"
                    >
                        <TextArea
                            rows={4}
                            placeholder="2025年春节主题素材&#10;2025年新年主题素材&#10;喜庆节日素材"
                        />
                    </Form.Item> */}
                </Form>
            </Modal>

            {/* 关键字管理对话框 */}
            <Modal
                title={`管理关键字 - ${selectedMaterial?.name}`}
                open={keywordModalVisible}
                onOk={handleAddKeywords}
                onCancel={() => setKeywordModalVisible(false)}
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>当前关键字：</div>
                    <Space wrap>
                        {selectedMaterial?.keywords?.map(kw => (
                            <Tag
                                key={kw.id}
                                closable
                                onClose={(e) => {
                                    e.preventDefault();
                                    if (selectedMaterial) {
                                        handleRemoveKeyword(selectedMaterial.id, kw.id);
                                    }
                                }}
                            >
                                {kw.keyword}
                            </Tag>
                        ))}
                        {(!selectedMaterial?.keywords || selectedMaterial.keywords.length === 0) && (
                            <span style={{ color: '#999' }}>暂无关键字</span>
                        )}
                    </Space>
                </div>

                <div>
                    <div style={{ marginBottom: 8 }}>添加关键字：</div>
                    <Select
                        mode="tags"
                        style={{ width: '100%' }}
                        placeholder="选择或输入新关键字"
                        value={selectedKeywords}
                        onChange={setSelectedKeywords}
                    >
                        {keywords.map(kw => (
                            <Option key={kw.id} value={kw.keyword}>
                                {kw.keyword} {kw.usage_count ? `(${kw.usage_count})` : ''}
                            </Option>
                        ))}
                    </Select>
                    <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
                        可以选择已有关键字，也可以输入新关键字后按回车
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default MaterialManagement;

